/* eslint-disable max-len */
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { StoreModule } from '@ngrx/store';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { TextMaskModule } from 'angular2-text-mask';
import { MDBBootstrapModulesPro, ToastModule } from 'ng-uikit-pro-standard';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgxJsonLdModule } from '@ngx-lite/json-ld';
import { TextareaAutosizeModule } from 'ngx-textarea-autosize';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { InstanceIdentifierInterceptor } from './instance-identifier-interceptor';
import { ActionThankYouComponent } from './jobs/action-thank-you/action-thank-you.component';
import { ApplicantMessageComponent } from './jobs/applicant-message/applicant-message.component';
import { ApplyEducationHistoryComponent } from './jobs/apply-education-history/apply-education-history.component';
import { EducationComponent } from './jobs/apply-education-history/education/education.component';
import { ApplyEmploymentHistoryComponent } from './jobs/apply-employment-history/apply-employment-history.component';
import { ExperienceComponent } from './jobs/apply-employment-history/experience/experience.component';
import { ApplyPersonalInformationComponent } from './jobs/apply-personal-information/apply-personal-information.component';
import { ApplyReferenceComponent } from './jobs/apply-reference/apply-reference.component';
import { ReferenceComponent } from './jobs/apply-reference/reference/reference.component';
import { ApplyResumeComponent } from './jobs/apply-resume/apply-resume.component';
import { ApplySurveyComponent } from './jobs/apply-survey/apply-survey.component';
import { CustomApplicantElementRendererComponent } from './jobs/apply-survey/custom-applicant-element-renderer/custom-applicant-element-renderer.component';
import { QuestionHostDirective } from './jobs/apply-survey/custom-applicant-element-renderer/question-host.directive';
import { AutoScoreComponent } from './jobs/apply-survey/custom-applicant-element-renderer/questions/auto-score/auto-score.component';
import { FileUploadComponent } from './jobs/apply-survey/custom-applicant-element-renderer/questions/file-upload/file-upload.component';
import { InformationComponent } from './jobs/apply-survey/custom-applicant-element-renderer/questions/information/information.component';
import { MultipleChoiceAutoScoreComponent } from './jobs/apply-survey/custom-applicant-element-renderer/questions/multiple-choice-auto-score/multiple-choice-auto-score.component';
import { MultipleChoiceComponent } from './jobs/apply-survey/custom-applicant-element-renderer/questions/multiple-choice/multiple-choice.component';
import { TextComponent } from './jobs/apply-survey/custom-applicant-element-renderer/questions/text/text.component';
import { YesNoComponent } from './jobs/apply-survey/custom-applicant-element-renderer/questions/yes-no/yes-no.component';
import { ApplyVoluntaryIdentificationComponent } from './jobs/apply-voluntary-identification/apply-voluntary-identification.component';
import { ContinueSavedApplicationComponent } from './jobs/continue-saved-application/continue-saved-application.component';
import { DirectJobLinkComponent } from './jobs/direct-job-link/direct-job-link.component';
import { IterativeApplicationLandingComponent } from './jobs/iterative-application-landing/iterative-application-landing.component';
import { JobClosedComponent } from './jobs/job-closed/job-closed.component';
import { JobNotFoundComponent } from './jobs/job/job-not-found/job-not-found.component';
import { JobComponent } from './jobs/job/job.component';
import { JobsComponent } from './jobs/jobs.component';
import { OfferComponent } from './jobs/offer/offer.component';
import { ResumeParsingStateComponent } from './jobs/resume-parsing-state/resume-parsing-state.component';
import { ReviewAndSubmitComponent } from './jobs/review-and-submit/review-and-submit.component';
import { SaveAndFinishLaterComponent } from './jobs/save-and-finish-later/save-and-finish-later.component';
import { ScheduleComponent } from './jobs/schedule/schedule.component';
import { SubmitIterativeApplicationPartComponent } from './jobs/submit-iterative-application-part/submit-iterative-application-part.component';
import { SubmitReferenceComponent } from './jobs/submit-reference/submit-reference.component';
import { ThankYouComponent } from './jobs/thank-you/thank-you.component';
import { ApplicantFileLayoutComponent } from './layouts/applicant-file-layout/applicant-file-layout.component';
import { ApplicationLayoutComponent } from './layouts/application-layout/application-layout.component';
import { YellowBrickRoadComponent } from './layouts/application-layout/yellow-brick-road/yellow-brick-road.component';
import { FooterComponent } from './layouts/footer/footer.component';
import { HeaderComponent } from './layouts/header/header.component';
import { LogDnaErrorHandler } from './logdna-error-handler';
import { ERROR_HANDLERS, MultiErrorHandler } from './multi-error-handler';
import { NewRelicErrorHandler } from './new-relic-error-handler';
import { AppDatatableFooterComponent } from './shared/components/app-datatable-footer/app-datatable-footer.component';
import { AttachmentsComponent } from './shared/components/attachments/attachments.component';
import { VideoPlayerComponent } from './shared/components/video-player/video-player.component';
import { ApplicationDeadlinePipe } from './shared/pipes/application-deadline.pipe';
import { CallbackPipe } from './shared/pipes/callback.pipe';
import { CustomDateFormatPipe } from './shared/pipes/custom-date-format.pipe';
import { AppInitializerService } from './shared/services/app-initializer.service';
import { AppService } from './shared/services/app.service';
import { FeatureFlagService } from './shared/services/feature-flag.service';
import { ShellComponent } from './shell/shell.component';
import { reducers } from './state';
import { VideoRecorderComponent } from './shared/components/video-recorder/video-recorder.component';
import { VideoComponent } from './jobs/apply-survey/custom-applicant-element-renderer/questions/video/video.component';
import { ShortTextComponent } from './jobs/apply-survey/custom-applicant-element-renderer/questions/short-text/short-text.component';

/* eslint-enable max-len */

@NgModule({
    declarations: [
        AppComponent,
        JobsComponent,
        ApplyResumeComponent,
        ApplySurveyComponent,
        QuestionHostDirective,
        CustomApplicantElementRendererComponent,
        TextComponent,
        AutoScoreComponent,
        YesNoComponent,
        FileUploadComponent,
        MultipleChoiceComponent,
        ApplicationLayoutComponent,
        ApplyPersonalInformationComponent,
        YellowBrickRoadComponent,
        ResumeParsingStateComponent,
        ReviewAndSubmitComponent,
        ApplyEmploymentHistoryComponent,
        ExperienceComponent,
        ApplyEducationHistoryComponent,
        EducationComponent,
        IterativeApplicationLandingComponent,
        ThankYouComponent,
        SubmitIterativeApplicationPartComponent,
        ApplyReferenceComponent,
        ReferenceComponent,
        ScheduleComponent,
        ApplicantFileLayoutComponent,
        OfferComponent,
        JobComponent,
        FooterComponent,
        HeaderComponent,
        CustomDateFormatPipe,
        ApplicationDeadlinePipe,
        AppDatatableFooterComponent,
        CallbackPipe,
        JobClosedComponent,
        DirectJobLinkComponent,
        ShellComponent,
        ApplyVoluntaryIdentificationComponent,
        InformationComponent,
        ApplicantMessageComponent,
        AttachmentsComponent,
        SubmitReferenceComponent,
        ActionThankYouComponent,
        JobNotFoundComponent,
        MultipleChoiceAutoScoreComponent,
        SaveAndFinishLaterComponent,
        ContinueSavedApplicationComponent,
        VideoPlayerComponent,
        VideoRecorderComponent,
        VideoComponent,
        ShortTextComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        MDBBootstrapModulesPro.forRoot(),
        ToastModule.forRoot(),
        HttpClientModule,
        FormsModule,
        TextMaskModule,
        TextareaAutosizeModule,
        NgxDatatableModule,
        NgSelectModule,
        CKEditorModule,
        NgxJsonLdModule,
        StoreModule.forRoot(reducers, {})
    ],
    providers: [
        AppService,
        AppInitializerService,
        FeatureFlagService,
        DeviceDetectorService,
        { provide: HTTP_INTERCEPTORS, useClass: InstanceIdentifierInterceptor, multi: true },
        { provide: ERROR_HANDLERS, useClass: NewRelicErrorHandler, multi: true },
        { provide: ERROR_HANDLERS, useClass: LogDnaErrorHandler, multi: true },
        { provide: ErrorHandler, useClass: MultiErrorHandler },
        {
            provide: APP_INITIALIZER,
            useFactory: (appInitializer: AppInitializerService) => () => appInitializer.getApplication(),
            deps: [AppInitializerService],
            multi: true
        }
    ],
    bootstrap: [AppComponent],
    schemas: [NO_ERRORS_SCHEMA]
})
export class AppModule { }
